import React, { useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import EmailRoundedIcon from '@material-ui/icons/EmailRounded';
import LockRoundedIcon from '@material-ui/icons/LockRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import BarChartIcon from '@material-ui/icons/BarChart';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import SecurityIcon from '@material-ui/icons/Security';
import { Link } from 'react-router-dom';
import { Input } from '@rocketseat/unform';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import api from '../../../services/api';
import { phoneMask } from '../../../utils/functions';
import {
  Container,
  MarketingSide,
  FormSide,
  FormCard,
  FormLogin,
  useStyles,
} from './RegisterStyles';
import logoimage from '../../../files/images/logo.png';
import dashImage from './Assets/dashDesktop.png';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('Insira um e-mail válido')
    .required('O e-mail é obrigatório'),
  password: Yup.string().required('A senha é obrigatória'),
  name: Yup.string().required('O nome é obrigatório'),
});

export default function Register() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [emailFocus, setEmailFocus] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [nameFocus, setNameFocus] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [phoneFocus, setPhoneFocus] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');

  function handleSubmit(data: any): void {
    const { email, password, name } = data;
    const phone = phoneValue.replace(/\D/g, '');
    if (!phone) {
      toast.error('O telefone é obrigatório');
      return;
    }
    setLoading(true);

    api
      .post('register', { name, phone, email, password })
      .then(() => {
        setLoading(false);
        setRegisteredEmail(email);
        setRegistered(true);
      })
      .catch(err => {
        setLoading(false);
        toast.error(err.response.data.error);
      });
  }

  function onInputFocus(type: string): void {
    if (type === 'email') setEmailFocus(true);
    else if (type === 'name') setNameFocus(true);
    else if (type === 'phone') setPhoneFocus(true);
    else setPasswordFocus(true);
  }

  function onInputBlur(type: string): void {
    if (type === 'email' && !emailValue) setEmailFocus(false);
    if (type === 'password' && !passwordValue) setPasswordFocus(false);
    if (type === 'phone' && !phoneValue) setPhoneFocus(false);
    if (type === 'name' && !nameValue) setNameFocus(false);
  }

  if (registered) {
    return (
      <div className={classes.verifyContainer}>
        <div className={classes.verifyCard}>
          <EmailRoundedIcon style={{ fontSize: '56px', color: '#ffee58', marginBottom: '16px' }} />
          <Typography style={{ fontWeight: 700, fontSize: '22px', marginBottom: '12px' }}>
            Verifique seu email
          </Typography>
          <Typography variant="body2" style={{ color: '#ffffffcc', marginBottom: '4px' }}>
            Enviamos um link de confirmação para
          </Typography>
          <Typography style={{ color: '#ffee58', fontWeight: 700, fontSize: '15px', marginBottom: '16px' }}>
            {registeredEmail}
          </Typography>
          <Typography variant="body2" style={{ color: '#ffffff77', lineHeight: 1.6 }}>
            Clique no link do email para ativar sua conta.<br />Verifique também a caixa de spam.
          </Typography>
          <Link
            to="/login"
            style={{
              marginTop: '28px',
              padding: '10px 32px',
              background: '#ffee58',
              borderRadius: '10px',
              color: '#1E2141',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Container>
      <MarketingSide>
        <img src={logoimage} alt="DashTrader" className={classes.marketingLogo} />
        <div className={classes.heroImageWrapper}>
          <img src={dashImage} alt="DashTrader Dashboard" className={classes.heroImage} />
        </div>
        <Typography className={classes.heroSubtitle}>
          Crie sua conta e tenha acesso a um dashboard completo para gerenciar e analisar todos os seus trades.
        </Typography>
        <div className={classes.featureList}>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><TrendingUpIcon style={{ fontSize: '16px' }} /></div>
            Acompanhamento de lucro e prejuízo em tempo real
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><BarChartIcon style={{ fontSize: '16px' }} /></div>
            Estatísticas detalhadas por ativo, horário e tipo
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><TrackChangesIcon style={{ fontSize: '16px' }} /></div>
            Metas diárias, semanais e mensais com progresso
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><SecurityIcon style={{ fontSize: '16px' }} /></div>
            Stop Win e Stop Loss para proteger seus resultados
          </div>
        </div>
      </MarketingSide>

      <FormSide>
        <FormCard>
          <img src={logoimage} alt="DashTrader" className={classes.logo} />
          <Typography className={classes.welcomeText}>
            Crie sua conta gratuita e comece agora
          </Typography>

          <FormLogin onSubmit={handleSubmit} schema={schema}>
            <div className="input-wrapper">
              <label>Nome</label>
              <div className={`input-field ${nameFocus ? 'focus' : ''}`}>
                <PersonRoundedIcon />
                <Input
                  onFocus={() => onInputFocus('name')}
                  onBlur={() => onInputBlur('name')}
                  name="name"
                  type="text"
                  autoFocus
                  spellCheck={false}
                  placeholder="Seu nome completo"
                  onChange={(e: any) => setNameValue(e.target.value)}
                />
              </div>
            </div>

            <div className="input-wrapper">
              <label>Telefone</label>
              <div className={`input-field ${phoneFocus ? 'focus' : ''}`}>
                <WhatsAppIcon />
                <input
                  onFocus={() => onInputFocus('phone')}
                  onBlur={() => onInputBlur('phone')}
                  type="text"
                  spellCheck={false}
                  placeholder="(00) 00000-0000"
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(phoneMask(e.target.value))}
                />
              </div>
            </div>

            <div className="input-wrapper">
              <label>Email</label>
              <div className={`input-field ${emailFocus ? 'focus' : ''}`}>
                <EmailRoundedIcon />
                <Input
                  onFocus={() => onInputFocus('email')}
                  onBlur={() => onInputBlur('email')}
                  name="email"
                  type="email"
                  spellCheck={false}
                  placeholder="seu@email.com"
                  onChange={(e: any) => setEmailValue(e.target.value)}
                />
              </div>
            </div>

            <div className="input-wrapper">
              <label>Senha</label>
              <div className={`input-field ${passwordFocus ? 'focus' : ''}`}>
                <LockRoundedIcon />
                <Input
                  onFocus={() => onInputFocus('password')}
                  onBlur={() => onInputBlur('password')}
                  name="password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  onChange={(e: any) => setPasswordValue(e.target.value)}
                />
              </div>
            </div>

            <Button
              className="submitButton"
              variant="contained"
              color="primary"
              type="submit"
              disabled={!!loading}
            >
              {loading ? <CircularProgress size={22} /> : 'Criar conta gratuita'}
            </Button>
          </FormLogin>

          <div className={classes.footer}>
            Já possui uma conta?{' '}
            <Link to="/login">Entrar agora</Link>
          </div>
        </FormCard>
      </FormSide>
    </Container>
  );
}
